import { useEffect } from 'react'
import { Alert, Col, DatePicker, Divider, Form, Input, InputNumber, Modal, Radio, Row, Select, Skeleton } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import dayjs, { type Dayjs } from 'dayjs'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { tenantToday } from '@/shared/lib/dayRange'
import { getCurrencyCode } from '@/shared/lib/format'
import type { Money } from '@/shared/lib/money'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { useRequiredFields } from '@/features/subscriber/formSettingsApi'
import {
  CONTRACT_KINDS,
  CONTRACT_TERMS,
  createContract,
  fetchPerson,
  savePerson,
  updateContract,
  type Contract,
  type ContractDraft,
  type ContractKind,
  type PersonDetails,
} from './contractApi'

const MONEY_FIELDS = [
  'packagePrice',
  'routerPrice',
  'devicePrice',
  'cablePrice',
  'installationTotal',
  'paidAmount',
  'remainingAmount',
] as const
type MoneyField = (typeof MONEY_FIELDS)[number] | 'depositAmount' | 'monthlyRental'

interface FormValues extends PersonDetails {
  kind: ContractKind
  startsOn: Dayjs
  termYears: number
  routerType?: string
  receiverDevice?: string
  deviceMac?: string
  cableLengthM?: number | null
  guarantorName?: string
  guarantorRelation?: string
  guarantorPhone?: string
  note?: string
  money: Partial<Record<MoneyField, Money | null>>
}

interface ContractFormModalProps {
  open: boolean
  subscriberId: string
  contract: Contract | null
  onClose: () => void
}

const MAC = /^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/

/** Shartnoma tuzish yoki tahrirlash: abonentning shaxsiy ma'lumoti va bitim (ADR-051). */
export function ContractFormModal({ open, subscriberId, contract, onClose }: ContractFormModalProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()
  const kind = Form.useWatch('kind', form)
  const personKind = Form.useWatch('personKind', form)
  const currency = contract?.currency ?? user?.tenant.currency ?? getCurrencyCode()
  const requiredFields = useRequiredFields(open)
  const personRules = (field: string) =>
    requiredFields.has(field) ? [{ required: true, whitespace: true, message: t('contract.fieldRequired') }] : []

  const person = useQuery({
    queryKey: queryKeys.subscribers.person(subscriberId),
    queryFn: ({ signal }) => fetchPerson(subscriberId, signal),
    enabled: open,
  })

  useEffect(() => {
    if (!open || !person.data) return
    const money = (value: number | null | undefined): Money | null =>
      value == null ? null : { amount: value, currency }
    form.setFieldsValue({
      ...person.data,
      personKind: person.data.personKind ?? 'INDIVIDUAL',
      kind: contract?.kind ?? 'SALE',
      startsOn: contract ? dayjs(contract.startsOn) : tenantToday(),
      termYears: contract?.termYears ?? 1,
      routerType: contract?.routerType ?? undefined,
      receiverDevice: contract?.receiverDevice ?? undefined,
      deviceMac: contract?.deviceMac ?? undefined,
      cableLengthM: contract?.cableLengthM ?? null,
      guarantorName: contract?.guarantorName ?? undefined,
      guarantorRelation: contract?.guarantorRelation ?? undefined,
      guarantorPhone: contract?.guarantorPhone ?? undefined,
      note: contract?.note ?? undefined,
      money: {
        packagePrice: money(contract?.packagePrice),
        routerPrice: money(contract?.routerPrice),
        devicePrice: money(contract?.devicePrice),
        cablePrice: money(contract?.cablePrice),
        installationTotal: money(contract?.installationTotal),
        paidAmount: money(contract?.paidAmount),
        remainingAmount: money(contract?.remainingAmount),
        depositAmount: money(contract?.depositAmount),
        monthlyRental: money(contract?.monthlyRental),
      },
    })
  }, [open, person.data, contract, currency, form])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const amount = (field: MoneyField) => values.money[field]?.amount ?? null
      const details: PersonDetails = {
        personKind: values.personKind ?? null,
        companyName: values.personKind === 'LEGAL' ? values.companyName ?? null : null,
        licenseNumber: values.personKind === 'LEGAL' ? values.licenseNumber ?? null : null,
        directorName: values.personKind === 'LEGAL' ? values.directorName ?? null : null,
        signerPosition: values.personKind === 'LEGAL' ? values.signerPosition ?? null : null,
        nationalId: values.nationalId ?? null,
        occupation: values.occupation ?? null,
        education: values.education ?? null,
        familyPhone: values.familyPhone ?? null,
        installVillage: values.installVillage ?? null,
        installStreet: values.installStreet ?? null,
        installHouse: values.installHouse ?? null,
      }
      const draft: ContractDraft = {
        kind: values.kind,
        startsOn: values.startsOn.format('YYYY-MM-DD'),
        termYears: values.termYears,
        routerType: values.routerType ?? null,
        receiverDevice: values.receiverDevice ?? null,
        deviceMac: values.deviceMac ?? null,
        cableLengthM: values.cableLengthM ?? null,
        ...Object.fromEntries(MONEY_FIELDS.map((field) => [field, amount(field)])),
        depositAmount: values.kind === 'GUARANTEE' ? amount('depositAmount') : null,
        monthlyRental: values.kind === 'RENTAL' ? amount('monthlyRental') : null,
        guarantorName: values.guarantorName ?? null,
        guarantorRelation: values.guarantorRelation ?? null,
        guarantorPhone: values.guarantorPhone ?? null,
        note: values.note ?? null,
      }
      await savePerson(subscriberId, details)
      return contract ? updateContract(contract.id, draft) : createContract(subscriberId, draft)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.contracts(subscriberId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.person(subscriberId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.contracts.all })
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  const guarantorRequired = kind === 'AMANAT' || kind === 'RENTAL'
  const moneyItem = (field: MoneyField, required = false) => (
    <Col xs={24} sm={12} key={field}>
      <Form.Item
        name={['money', field]}
        label={t(`contract.fields.${field}`)}
        rules={required ? [{ required: true, message: t('contract.amountRequired') }] : []}
      >
        <MoneyInput currency={currency} minMinor={required ? 1 : 0} style={{ width: '100%' }} />
      </Form.Item>
    </Col>
  )

  return (
    <Modal
      open={open}
      width={860}
      title={contract ? t('contract.editTitle', { number: contract.number }) : t('contract.createTitle')}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={save.isPending}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      destroyOnHidden
    >
      {save.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(save.error, t)} />
      ) : null}
      {person.isError ? <Alert type="error" showIcon message={translateError(person.error, t)} /> : null}
      {person.isPending ? (
        <Skeleton active />
      ) : (
        <Form<FormValues> form={form} layout="vertical" requiredMark preserve={false}>
          <Divider orientation="left" plain>
            {t('contract.sections.person')}
          </Divider>
          <Form.Item name="personKind" label={t('contract.fields.personKind')}>
            <Radio.Group
              optionType="button"
              options={(['INDIVIDUAL', 'LEGAL'] as const).map((value) => ({
                value,
                label: t(`contract.personKind.${value}`),
              }))}
            />
          </Form.Item>
          <Row gutter={16}>
            {personKind === 'LEGAL'
              ? (['companyName', 'licenseNumber', 'directorName', 'signerPosition'] as const).map((field) => (
                  <Col xs={24} sm={12} key={field}>
                    <Form.Item name={field} label={t(`contract.fields.${field}`)}>
                      <Input maxLength={field === 'companyName' ? 200 : 160} />
                    </Form.Item>
                  </Col>
                ))
              : null}
            {(['nationalId', 'occupation', 'education', 'familyPhone'] as const).map((field) => (
              <Col xs={24} sm={12} key={field}>
                <Form.Item name={field} label={t(`contract.fields.${field}`)} rules={personRules(field)}>
                  <Input maxLength={field === 'nationalId' ? 40 : 100} />
                </Form.Item>
              </Col>
            ))}
            {(['installVillage', 'installStreet', 'installHouse'] as const).map((field) => (
              <Col xs={24} sm={8} key={field}>
                <Form.Item name={field} label={t(`contract.fields.${field}`)} rules={personRules(field)}>
                  <Input maxLength={160} />
                </Form.Item>
              </Col>
            ))}
          </Row>

          <Divider orientation="left" plain>
            {t('contract.sections.contract')}
          </Divider>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="kind" label={t('contract.fields.kind')} extra={kind ? t(`contract.kindHint.${kind}`) : null}>
                <Select<ContractKind>
                  options={CONTRACT_KINDS.map((value) => ({ value, label: t(`contract.kind.${value}`) }))}
                />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item name="startsOn" label={t('contract.fields.startsOn')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} allowClear={false} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6}>
              <Form.Item name="termYears" label={t('contract.fields.termYears')}>
                <Radio.Group
                  optionType="button"
                  options={CONTRACT_TERMS.map((value) => ({ value, label: t('contract.years', { count: value }) }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="receiverDevice" label={t('contract.fields.receiverDevice')}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="routerType" label={t('contract.fields.routerType')}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="deviceMac"
                label={t('contract.fields.deviceMac')}
                rules={[{ pattern: MAC, message: t('contract.macInvalid') }]}
              >
                <Input maxLength={17} placeholder="AA:BB:CC:DD:EE:FF" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="cableLengthM" label={t('contract.fields.cableLengthM')}>
                <InputNumber min={0} max={100000} precision={0} style={{ width: '100%' }} addonAfter="m" />
              </Form.Item>
            </Col>
            {kind === 'GUARANTEE' ? moneyItem('depositAmount', true) : null}
            {kind === 'RENTAL' ? moneyItem('monthlyRental', true) : null}
            {MONEY_FIELDS.map((field) => moneyItem(field))}
          </Row>
          <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('contract.pricesInfo')} />

          <Divider orientation="left" plain>
            {t('contract.sections.guarantor')}
          </Divider>
          <Row gutter={16}>
            <Col xs={24} sm={10}>
              <Form.Item
                name="guarantorName"
                label={t('contract.fields.guarantorName')}
                rules={guarantorRequired ? [{ required: true, whitespace: true, message: t('contract.guarantorRequired') }] : []}
              >
                <Input maxLength={160} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item name="guarantorRelation" label={t('contract.fields.guarantorRelation')}>
                <Input maxLength={60} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="guarantorPhone"
                label={t('contract.fields.guarantorPhone')}
                rules={guarantorRequired ? [{ required: true, whitespace: true, message: t('contract.guarantorRequired') }] : []}
              >
                <Input maxLength={32} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="note" label={t('contract.fields.note')}>
            <Input.TextArea rows={2} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
